import { createContext, useEffect } from 'react';
import ReturnViewModel from './ReturnViewModel';
import { trackException } from '../../applicationInsights';

type ContextModel = {
    logStartActivity: (discriminator: string, details: string, setActivityId: (activityId: string) => void) => void;
    logEndActivity: (activityId: string, clearActivityId: () => void) => void;
    logAPICall: (type: 'LogAPICall' | 'LogCount', discriminator: string, details: string, response: string, duration: number) => void;
};

export const SystemLogContext = createContext<ContextModel>({
    logStartActivity: () => { throw Error('Cannot use a context without a provider'); },
    logEndActivity: () => { throw Error('Cannot use a context without a provider'); },
    logAPICall: () => { throw Error('Cannot use a context without a provider'); },
});

export default function SystemLogContextProvider({ children }: React.PropsWithChildren<{}>) {
    useEffect(() => {
        const handleBeforeUnload = () => {
            navigator.sendBeacon(
                '/api/TelemetrySession/end-session',
                new Blob([JSON.stringify({ reason: 'PageClose' })], { type: 'application/json' })
            );
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    const logStartActivity = async (discriminator: string, details: string, setActivityId: (activityId: string) => void) => {
        const apiPath = `SystemLog/StartActivity`;
        const split = discriminator.split('?');
        const body = JSON.stringify(new LogViewModel(split[0], split[1] || '', details));
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: body,
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (result.success) {
                setActivityId(result.value);
            } else {
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        }
    }

    const logEndActivity = async (activityId: string, clearActivityId: () => void) => {
        const apiPath = `SystemLog/EndActivity`;
        try {
            const response = await fetch(`api/${apiPath}?ActivityId=${activityId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (result.success) {
                clearActivityId();
            } else {
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        }
    }

    const logDiscriminators = [
        'sitelist/getsitelist',
        'sitelist/getsitemap',
        'site/getsite',
        'tripratecalculation/gettripratecalculation',
        'tripratefilter/getselectedsurveys',
        'rankorder/getrankordertable'
    ]

    const logAPICall = async (type: 'LogAPICall' | 'LogCount', discriminator: string, details: string, response: string, duration: number) => {
        const split = discriminator.split('?');
        if (!logDiscriminators.includes(split[0].toLowerCase()) && type == 'LogAPICall') {
            return; // Skip logging for non-tracked discriminators
        }
        const body = JSON.stringify(new LogViewModel(split[0], split[1] || '', details, response, duration));
        const apiPath = `SystemLog/${type}`;
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: body,
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (!result.success) {
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        }
    }

    const context: ContextModel = {
        logStartActivity,
        logEndActivity,
        logAPICall
    }

    return (
        <SystemLogContext.Provider value={context}>
            {children}
        </SystemLogContext.Provider>
    );
}

export class LogViewModel {
    public discriminator: string = '';
    public query: string = '';
    public details: string = '';
    public response?: string;
    public duration?: number;
    constructor(discriminator: string, query: string, details: string, response?: string, duration?: number) {
        this.discriminator = discriminator;
        this.query = query;
        this.details = details;
        this.response = response;
        this.duration = duration;
    }
}
